import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash.debounce';
import Scrollbar from 'react-scrollbars-custom';
import usePrevious from 'src/hooks/usePrevious';
import isEqual from 'lodash.isequal';
import { setScrollYPosition, setScrollXPosition } from 'src/redux/configurator/actions';
import { getScrollYPosition, getScrollXPosition } from 'src/redux/configurator/selectors';
import Label from 'components/_utility/Label';
import { Card } from '@material-ui/core';
import { Outer, Wrap, Gutter, ResultCount } from './CardList.styled';

const CardList = ({
  cardComponent: CardComponent,
  cards = [],
  selectedCardIds = [],
  onSelectCard = () => {},
  onInfoClick = () => {},
  horizontalScroll = false,
  searchQuery = '',
  showResults = false,
  packs = false,
  isAccessoryCard,
  ...props
}) => {
  const dispatch = useDispatch();
  const scrollbarRef = useRef();
  const previousCards = usePrevious(cards);
  const scrollYPosition = useSelector(getScrollYPosition);
  const prevScrollYPosition = usePrevious(scrollYPosition);
  const scrollXPosition = useSelector(getScrollXPosition);
  const prevScrollXPosition = usePrevious(scrollXPosition);

  const handleScroll = debounce(event => {
    const isNewCardDeck = previousCards && !isEqual(previousCards, cards) && typeof prevScrollYPosition !== 'number';

    dispatch(setScrollYPosition(isNewCardDeck ? 0 : event.scrollTop));
    dispatch(setScrollXPosition(isNewCardDeck ? 0 : event.scrollLeft));
  }, 100);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    if (scrollYPosition && !prevScrollYPosition) {
      scrollbarRef.current.scrollTo(0, scrollYPosition);
    }

    if (scrollXPosition && !prevScrollXPosition) {
      scrollbarRef.current.scrollTo(scrollXPosition, 0);
    }

    if (previousCards && !isEqual(previousCards, cards)) {
      scrollbarRef.current.scrollToTop();
      scrollbarRef.current.scrollToLeft();
      dispatch(setScrollYPosition(0));
      dispatch(setScrollXPosition(0));
    }
  }, [cards, dispatch, prevScrollXPosition, prevScrollYPosition, previousCards, scrollXPosition, scrollYPosition]);

  return (
    <Outer
      horizontalScroll={horizontalScroll}
      packs={packs}
      showResults={showResults}
      resultCount={cards.length}
      isAccessoryCard
    >
      <Scrollbar ref={scrollbarRef} onScroll={handleScroll} noDefaultStyles>
        {showResults ? (
          <ResultCount resultCount={cards.length}>
            {cards.length ? (
              <span>
                {cards.length} <Label name="resultsLabel" />
              </span>
            ) : (
              <span>
                <Label name="accessoryNoResultsLabel" />
              </span>
            )}{' '}
            <Label name="forLabel" /> '{searchQuery}'
          </ResultCount>
        ) : null}
        {cards.length ? (
          <Wrap horizontalScroll={horizontalScroll} cardCount={cards.length} packs={packs} isAccessoryCard>
            {cards.map((card, index) => (
              <CardComponent
                key={card.id}
                selected={selectedCardIds.includes(card.id)}
                onSelectCard={onSelectCard}
                onInfoClick={onInfoClick}
                cardIndex={index}
                horizontalScroll={horizontalScroll}
                scrollX={scrollXPosition}
                scrollY={scrollYPosition}
                {...card}
                {...props}
              />
            ))}
            <Gutter horizontalScroll={horizontalScroll} packs={packs} />
          </Wrap>
        ) : null}
      </Scrollbar>
    </Outer>
  );
};

export default CardList;
